import api from './api';

export interface Imputable {
    nroCuenta: number;
    nroRubro?: number;
    nomCuenta?: string;
    tipoCuentaImputa?: string;
    descripcioncta?: string | null;
    visibleEn?: string;
    saldo?: number;
}

export interface ImputableCRUD {
    codImputable: string;
    nombre: string;
    imputable: string;
    tipo: string;
    activo: string;
    /** Valor crudo de la API / BD (Ambos, IVarios, EVarios, etc.). */
    visibleEn?: string | null;
    codImputablePadre?: string;
    descripcion?: string;
    nroRubro?: number;
    saldo?: number | null;
}

export interface ImputableSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    // Corresponde al filtro que antes se aplicaba en el cliente sobre VisibleEn.
    // - 'I' => VisibleEn in ('IVarios','Ambos')
    // - 'E' => VisibleEn in ('EVarios','Ambos')
    tipoMovimientoIE?: 'I' | 'E';
}

export interface ImputablePagedResult {
    items: ImputableCRUD[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface ImputableSearchPagedResult {
    items: Imputable[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

const MAX_NOM_CUENTA_LEN = 60;

/** Alineado a [MaxLength(60)] en ImputableDto.NomCuenta. */
function trimNomCuenta(nombre: string | null | undefined): string {
    return String(nombre ?? '').trim().slice(0, MAX_NOM_CUENTA_LEN);
}

/** Deriva columna «Activo»: sin visibilidad o explícitamente Ninguno → N. */
function activoFromVisibleEn(visibleEn: string | null | undefined): string {
    const v = visibleEn != null ? String(visibleEn).trim() : '';
    if (!v || /^ninguno$/i.test(v)) return 'N';
    return 'S';
}

/** Código en BD: 1 Activo, 2 Pasivo, 3 Ingresos, 4 Egresos, 5 Patrimonio neto, 6 Unidad, 9 Cuenta y orden. Letras legacy A,P,R,O. */
export function tipoCuentaLabel(tipo: string | null | undefined): string {
    const t = String(tipo ?? '').trim();
    const codigo: Record<string, string> = {
        '1': 'Activo',
        '2': 'Pasivo',
        '3': 'Ingresos',
        '4': 'Egresos',
        '5': 'Patrimonio neto',
        '6': 'Unidad',
        '9': 'Cuenta y orden'
    };
    if (codigo[t]) return codigo[t];
    const legacy: Record<string, string> = {
        A: 'Activo',
        P: 'Pasivo',
        R: 'Resultado',
        O: 'Orden'
    };
    return legacy[t] ?? (t || '—');
}

/** Normaliza código, letra legacy o etiqueta de Excel/UI al código de 1 carácter que exige la API. */
export function normalizeTipoCuentaCodigo(tipo: string | null | undefined): string {
    const raw = String(tipo ?? '').trim();
    if (!raw) return '1';
    if (['1', '2', '3', '4', '5', '6', '9'].includes(raw)) return raw;

    // "1 — Activo", "3 - Ingresos", etc.
    const prefix = raw.match(/^([1234569])\s*[-–—]/);
    if (prefix) return prefix[1];

    const legacy: Record<string, string> = {
        A: '1',
        P: '2',
        R: '3',
        O: '9'
    };
    if (legacy[raw]) return legacy[raw];

    const byLabel: Record<string, string> = {
        activo: '1',
        pasivo: '2',
        ingresos: '3',
        egresos: '4',
        'patrimonio neto': '5',
        patrimonio: '5',
        unidad: '6',
        'cuenta y orden': '9',
        orden: '9',
        resultado: '3'
    };
    const key = raw.toLowerCase();
    return byLabel[key] ?? '1';
}

/** Valores permitidos para `VisibleEn` en imputables (coinciden con la BD / informes). */
export const VISIBLE_EN_OPTIONS = ['EVarios', 'Ninguno', 'IVarios', 'Ambos'] as const;
export type VisibleEnOption = (typeof VISIBLE_EN_OPTIONS)[number];

export function normalizeVisibleEnOption(v: string | null | undefined): VisibleEnOption {
    const s = String(v ?? '').trim();
    if ((VISIBLE_EN_OPTIONS as readonly string[]).includes(s)) return s as VisibleEnOption;
    const lower = s.toLowerCase();
    const aliases: Record<string, VisibleEnOption> = {
        evarios: 'EVarios',
        ninguno: 'Ninguno',
        ivarios: 'IVarios',
        ambos: 'Ambos'
    };
    return aliases[lower] ?? 'Ninguno';
}

function mapRawImputableItem(item: any): ImputableCRUD {
    const visNorm = normalizeVisibleEnOption(item.visibleEn);
    return {
        codImputable: item.codImputable || item.nroCuenta?.toString() || '',
        nombre: item.nombre || item.nomCuenta || '',
        imputable: item.imputable || (item.tipoCuentaImputa ? 'S' : 'N'),
        tipo: item.tipo || item.tipoCuentaImputa || '',
        activo: item.activo ?? activoFromVisibleEn(visNorm),
        visibleEn: visNorm,
        codImputablePadre: item.codImputablePadre,
        descripcion: item.descripcion || item.descripcioncta || '',
        nroRubro: item.nroRubro,
        saldo: item.saldo != null ? Number(item.saldo) : null
    };
}

/** Fila completa para informes PDF (incluye visibleEn crudo de la API). */
export interface ImputablePlanCuentasRow extends ImputableCRUD {
    visibleEn?: string | null;
}

const imputableService = {
    getImputables: async (params?: ImputableSearchParams): Promise<Imputable[]> => {
        const response = await api.get('/api/Imputables', params ? { params } : {});
        const data = response.data;
        
        if (Array.isArray(data)) {
            return data;
        }
        
        if (data && typeof data === 'object' && 'items' in data) {
            return data.items;
        }
        
        return [];
    },

    getImputablesSearchPaged: async (params: ImputableSearchParams): Promise<ImputableSearchPagedResult> => {
        const response = await api.get('/api/Imputables', { params });
        const data = response.data;
        const items: Imputable[] = Array.isArray(data?.items) ? data.items : [];
        const totalCount = data?.totalCount ?? 0;
        const pageSize = data?.pageSize ?? params.pageSize;
        const totalPages = (data?.totalPages ?? Math.ceil(totalCount / pageSize)) || 1;
        return {
            items,
            totalCount,
            pageNumber: data?.pageNumber ?? params.pageNumber,
            pageSize,
            totalPages
        };
    },

    getImputablesPaged: async (params: ImputableSearchParams): Promise<ImputablePagedResult> => {
        const response = await api.get('/api/Imputables', { params });
        const data = response.data;

        const transformItem = (item: any): ImputableCRUD => mapRawImputableItem(item);
        
        if (data && typeof data === 'object' && 'items' in data) {
            const transformedItems = Array.isArray(data.items) ? data.items.map(transformItem) : [];
            return {
                items: transformedItems,
                totalCount: data.totalCount || transformedItems.length,
                pageNumber: data.pageNumber || params.pageNumber,
                pageSize: data.pageSize || params.pageSize,
                totalPages: data.totalPages || Math.ceil((data.totalCount || transformedItems.length) / params.pageSize)
            };
        }
        
        if (Array.isArray(data)) {
            const transformedItems = data.map(transformItem);
            return {
                items: transformedItems,
                totalCount: data.length,
                pageNumber: params.pageNumber,
                pageSize: params.pageSize,
                totalPages: Math.ceil(data.length / params.pageSize)
            };
        }
        
        return {
            items: [],
            totalCount: 0,
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
            totalPages: 0
        };
    },

    /**
     * Obtiene todos los imputables recorriendo la paginación del backend (pageSize máx. 20 en API).
     * @param searchTerm Mismo filtro que el listado; vacío = todos.
     */
    getAllImputablesPlanCuentas: async (searchTerm: string = ''): Promise<ImputablePlanCuentasRow[]> => {
        const pageSize = 20;
        let pageNumber = 1;
        let totalCount = Infinity;
        const rows: ImputablePlanCuentasRow[] = [];
        const maxPages = 5000;
        const term = (searchTerm ?? '').trim();

        while (rows.length < totalCount && pageNumber <= maxPages) {
            const response = await api.get('/api/Imputables', {
                params: { pageNumber, pageSize, searchTerm: term }
            });
            const data = response.data;
            const rawItems: any[] = Array.isArray(data?.items) ? data.items : [];
            if (pageNumber === 1) {
                totalCount = typeof data?.totalCount === 'number' ? data.totalCount : rawItems.length;
            }
            for (const item of rawItems) {
                rows.push({
                    ...mapRawImputableItem(item),
                    visibleEn: item.visibleEn ?? null
                });
            }
            if (rawItems.length === 0) break;
            pageNumber += 1;
        }

        return rows;
    },

    getImputable: async (nroCuenta: number | string): Promise<(ImputableCRUD & Imputable) | null> => {
        try {
            const response = await api.get<Imputable>(`/api/Imputables/${nroCuenta}`);
            const item = response.data;
            if (!item) return null;
            const nro = item.nroCuenta ?? Number(nroCuenta);
            const nom = item.nomCuenta || '';
            const visibleNorm = normalizeVisibleEnOption(item.visibleEn);
            return {
                ...item,
                codImputable: item.nroCuenta?.toString() || String(nroCuenta),
                nombre: nom,
                imputable: item.tipoCuentaImputa ? 'S' : 'N',
                tipo: item.tipoCuentaImputa || '',
                visibleEn: visibleNorm,
                activo: activoFromVisibleEn(visibleNorm),
                codImputablePadre: undefined,
                descripcion: item.descripcioncta || '',
                nroRubro: item.nroRubro,
                saldo: item.saldo != null ? Number(item.saldo) : undefined,
                nroCuenta: nro,
                nomCuenta: nom
            };
        } catch (error) {
            console.error('Error al obtener imputable:', error);
            return null;
        }
    },

    createImputable: async (imputable: ImputableCRUD): Promise<ImputableCRUD> => {
        const payload = {
            nroCuenta: Number(imputable.codImputable),
            nroRubro: imputable.nroRubro ?? 0,
            nomCuenta: trimNomCuenta(imputable.nombre),
            tipoCuentaImputa: normalizeTipoCuentaCodigo(imputable.tipo),
            descripcioncta: imputable.descripcion ?? null,
            visibleEn: normalizeVisibleEnOption(imputable.visibleEn),
            saldo: imputable.saldo ?? null
        };
        const response = await api.post<ImputableCRUD>('/api/Imputables', payload);
        return response.data;
    },

    updateImputable: async (codImputable: string, imputable: ImputableCRUD): Promise<void> => {
        const visibleEn = normalizeVisibleEnOption(imputable.visibleEn);
        const payload = {
            nroRubro: imputable.nroRubro ?? 0,
            nomCuenta: trimNomCuenta(imputable.nombre),
            tipoCuentaImputa: normalizeTipoCuentaCodigo(imputable.tipo),
            descripcioncta: imputable.descripcion ?? null,
            visibleEn,
            saldo: imputable.saldo ?? null
        };
        await api.put(`/api/Imputables/${codImputable}`, payload);
    },

    deleteImputable: async (codImputable: string): Promise<void> => {
        await api.delete(`/api/Imputables/${codImputable}`);
    }
};

export default imputableService;

